import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Typography } from '@mui/material';

import type { TravelerDocumentMatchDto } from '@bandido/shared/dtos';

import { travelerDisplayName } from '../travelerForm';

interface Props {
  matches: TravelerDocumentMatchDto[];
  actionLabel: string;
  busy?: boolean;
  onUse: (match: TravelerDocumentMatchDto) => void;
}

/**
 * Fichas encontradas por documento. Muestra lo justo para confirmar que es la
 * misma persona: nombre, año de nacimiento y tipo y país de sus documentos.
 */
export const TravelerDocumentMatchList = ({
  matches,
  actionLabel,
  busy = false,
  onUse,
}: Props) => {
  const { t } = useTranslation();
  const documentLabel = (
    document: TravelerDocumentMatchDto['documents'][number],
  ) =>
    [
      t(`travelers.documents.types.${document.type}`, {
        defaultValue: document.type,
      }),
      document.issuingCountry,
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {matches.map((match) => (
        <Box
          key={match.travelerId}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          p={1.5}
          border={1}
          borderColor="divider"
          borderRadius={1}
          bgcolor="background.paper"
        >
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={600}>
              {travelerDisplayName(match)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[
                match.birthYear
                  ? t('travelers.documentMatches.birthYear', {
                    year: match.birthYear,
                  })
                  : t('travelers.noBirthDate'),
                ...match.documents.map((document) => documentLabel(document)),
              ].join(' · ')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              size="small"
              label={t(`travelers.documentMatches.relation.${match.relation}`)}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => onUse(match)}
            >
              {actionLabel}
            </Button>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
